import { useCallback } from 'react';
import type { RequestContext } from '../types';
import { USE_CASES, PLATFORM_USE_CASES, CUSTOM_USE_CASES } from '../constants/useCases';

interface Props {
  value: Partial<RequestContext>;
  onChange: (next: Partial<RequestContext>) => void;
  onOfferingChange: (offering: 'platform' | 'custom') => void;
}

export function RequestorContext({ value, onChange, onOfferingChange }: Props) {
  const update = useCallback(
    (patch: Partial<RequestContext>) => onChange({ ...value, ...patch }),
    [value, onChange],
  );

  const idTrimmed = (value.requestorId ?? '').trim();
  const idValid = /^L\d{6}$/i.test(idTrimmed);
  const idInvalid = idTrimmed.length > 0 && !idValid;

  return (
    <div className="card">
      <div className="requestor-banner">
        <div className="requestor-icon" aria-hidden>🪪</div>
        <div className="requestor-field">
          <label className="flbl" htmlFor="custId" style={{ marginBottom: 5 }}>
            Requestor Customer ID <span className="req">*</span>
          </label>
          <input
            id="custId"
            className={`requestor-id-input${idValid ? ' valid' : ''}${idInvalid ? ' invalid' : ''}`}
            type="text"
            placeholder="Enter Lilly Customer ID — e.g. L027231"
            maxLength={10}
            value={value.requestorId ?? ''}
            onChange={e => update({ requestorId: e.target.value })}
            aria-invalid={idInvalid}
            aria-describedby="custIdHint"
          />
          <div id="custIdHint" className="fhint" style={{ marginTop: 4 }}>
            Your Lilly employee ID is required to track the request and send you updates. Format: L
            followed by 6 digits.
          </div>
        </div>
      </div>

      <div className="flbl" style={{ marginBottom: 9 }}>
        Platform Offering Type <span className="req">*</span>
      </div>
      <div className="offering-toggle" role="radiogroup" aria-label="Platform Offering Type">
        <OfferingButton
          variant="platform"
          selected={value.offering === 'platform'}
          onClick={() => onOfferingChange('platform')}
        />
        <OfferingButton
          variant="custom"
          selected={value.offering === 'custom'}
          onClick={() => onOfferingChange('custom')}
        />
      </div>

      {value.offering === 'custom' && (
        <div className="custom-fields v">
          <div className="info-note">
            <span aria-hidden>🏷️</span>
            <span>
              <strong>Metric Tagging:</strong> Your team name is added as a tag to all metric
              time-series for identification in Splunk dashboards and alerts.
            </span>
          </div>
          <div className="fgrid2">
            <div className="field">
              <label className="flbl" htmlFor="teamName">
                Team Name <span className="req">*</span>
              </label>
              <input
                id="teamName"
                className="finput"
                type="text"
                placeholder="cam_hcs"
                value={value.teamName ?? ''}
                onChange={e => update({ teamName: e.target.value })}
              />
              <span className="fhint">e.g. cam_hcs — used as metric tag (lowercase, underscore-separated)</span>
            </div>
            <div className="field">
              <label className="flbl" htmlFor="lobInput">
                Line of Business <span className="req">*</span>
              </label>
              <input
                id="lobInput"
                className="finput"
                type="text"
                placeholder="e.g. Manufacturing & Quality"
                value={value.lineOfBusiness ?? ''}
                onChange={e => update({ lineOfBusiness: e.target.value })}
              />
              <span className="fhint">Minimum 3 characters required</span>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <div className="field">
          <label className="flbl" htmlFor="notifyEmail">
            Notify Email <span className="req">*</span>
          </label>
          <input
            id="notifyEmail"
            className="finput"
            type="email"
            placeholder="you@lilly.com"
            value={value.notifyEmail ?? ''}
            onChange={e => update({ notifyEmail: e.target.value })}
          />
        </div>
        <div className="field" style={{ marginTop: 14 }}>
          <label className="flbl" htmlFor="notesInput">
            Additional Notes
          </label>
          <textarea
            id="notesInput"
            className="finput"
            rows={3}
            placeholder="Any context for the platform team..."
            value={value.notes ?? ''}
            onChange={e => update({ notes: e.target.value })}
          />
        </div>
      </div>
    </div>
  );
}

interface OfferingButtonProps {
  variant: 'platform' | 'custom';
  selected: boolean;
  onClick: () => void;
}
function OfferingButton({ variant, selected, onClick }: OfferingButtonProps) {
  const isPlatform = variant === 'platform';
  return (
    <button
      type="button"
      className={`obtn${selected ? (isPlatform ? ' ap' : ' ac') : ''}`}
      onClick={onClick}
      role="radio"
      aria-checked={selected}
    >
      <div className={`obtn-icon ${isPlatform ? 'p' : 'c'}`} aria-hidden>
        {isPlatform ? '🏗️' : '⚙️'}
      </div>
      <div style={{ flex: 1 }}>
        <div className="obtn-title">
          {isPlatform ? 'Standard Platform Offering' : 'Custom Application Offering'}
        </div>
        <div className="obtn-desc">
          {isPlatform
            ? 'Managed by AIOps platform team. Production-ready, no team name required.'
            : 'Team-owned infrastructure. Team name tagged to all metric time-series.'}
        </div>
        <div className="obtn-tags">
          {(isPlatform ? PLATFORM_USE_CASES : CUSTOM_USE_CASES).map(k => (
            <span key={k} className={`otag ${isPlatform ? 'p' : 'c'}`}>
              {USE_CASES[k].label}
            </span>
          ))}
        </div>
      </div>
      <div className="ocheck" aria-hidden>✓</div>
    </button>
  );
}
